import type { AppContext } from '@application/context/AppContext';
import type { Role, User } from '@shared/types/User';
import {
  roleForTenant,
  sessionGrantsFromUser,
  withRoleForTenant,
} from '@shared/access/tenantUserRole';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { logger } from '@infra/telemetry/Logger';

export type CabinetAccessResult = { ok: true; user: User } | { ok: false; error: string };

/** Rôles qu'un octroi cabinet peut attribuer pour une entreprise (jamais super_admin). */
const GRANTABLE_ROLES: readonly Role[] = ['admin', 'rh', 'comptable', 'lecture'];

function addTenant(user: User, tenantId: string): User['accessible_tenant_ids'] {
  const current = user.accessible_tenant_ids;
  if (current === 'all') return 'all';
  return [...new Set([...(Array.isArray(current) ? current : []), tenantId])];
}

/**
 * Gestion des accès cabinet (P0-6) : `accessible_tenant_ids` et `roles_by_tenant` sont renseignés
 * explicitement. Rien n'est ouvert par défaut : un admin de l'entreprise A n'accède pas à B.
 */
export class CabinetAccessService {
  /**
   * Un administrateur de l'entreprise `tenantId` donne à un utilisateur l'accès à cette entreprise
   * avec un rôle précis. L'acteur doit lui-même être admin de cette entreprise.
   */
  static async grantTenantAccess(
    ctx: AppContext,
    input: { targetUserId: string; tenantId: string; role: Role },
  ): Promise<CabinetAccessResult> {
    if (!GRANTABLE_ROLES.includes(input.role)) {
      return { ok: false, error: 'Rôle non attribuable pour une autre entreprise.' };
    }
    const portfolio = await SecureLicenceStore.getActivationPortfolio();
    if (!(input.tenantId in portfolio)) {
      return { ok: false, error: 'Cette entreprise ne fait pas partie du portefeuille du poste.' };
    }
    const session = ctx.auth.getSession();
    const actor = session ? await ctx.userRepo.findById(session.user_id) : null;
    const actorRole = actor ? roleForTenant(actor, input.tenantId) : null;
    if (!actor || (actorRole !== 'admin' && actorRole !== 'super_admin')) {
      return { ok: false, error: 'Seul un administrateur de cette entreprise peut donner l’accès.' };
    }
    const target = await ctx.userRepo.findById(input.targetUserId);
    if (!target || !target.actif) return { ok: false, error: 'Utilisateur introuvable.' };

    const updated: User = {
      ...withRoleForTenant(target, input.tenantId, input.role),
      accessible_tenant_ids: addTenant(target, input.tenantId),
    };
    // withRoleForTenant ne change le rôle global que pour le tenant d'origine : inchangé ici.
    await ctx.userRepo.update(updated);
    logger.info({ event: 'cabinet_access_granted', tenantId: input.tenantId, role: input.role });
    return { ok: true, user: updated };
  }

  /** Retire l'accès d'un utilisateur à une entreprise (pas à sa propre entreprise d'origine). */
  static async revokeTenantAccess(
    ctx: AppContext,
    input: { targetUserId: string; tenantId: string },
  ): Promise<CabinetAccessResult> {
    const session = ctx.auth.getSession();
    const actor = session ? await ctx.userRepo.findById(session.user_id) : null;
    const actorRole = actor ? roleForTenant(actor, input.tenantId) : null;
    if (!actor || (actorRole !== 'admin' && actorRole !== 'super_admin')) {
      return { ok: false, error: 'Seul un administrateur de cette entreprise peut retirer l’accès.' };
    }
    const target = await ctx.userRepo.findById(input.targetUserId);
    if (!target) return { ok: false, error: 'Utilisateur introuvable.' };
    if (target.tenant_id === input.tenantId) {
      return { ok: false, error: 'L’accès à l’entreprise d’origine de l’utilisateur ne se retire pas.' };
    }
    const access = target.accessible_tenant_ids;
    const rolesByTenant = { ...(target.roles_by_tenant ?? {}) };
    delete rolesByTenant[input.tenantId];
    const updated: User = {
      ...target,
      roles_by_tenant: rolesByTenant,
      accessible_tenant_ids: Array.isArray(access)
        ? access.filter((id) => id !== input.tenantId)
        : access,
    };
    await ctx.userRepo.update(updated);
    return { ok: true, user: updated };
  }

  /**
   * À l'ajout d'une entreprise par sa clé commerciale : l'utilisateur qui vient de prouver la
   * possession de cette clé reçoit l'accès administrateur à cette entreprise (octroi explicite).
   */
  static async grantAfterKeyActivation(
    ctx: AppContext,
    userId: string,
    tenantId: string,
  ): Promise<CabinetAccessResult> {
    const user = await ctx.userRepo.findById(userId);
    if (!user || !user.actif) return { ok: false, error: 'Utilisateur introuvable.' };
    if (user.tenant_id === tenantId) return { ok: true, user };
    const updated: User = {
      ...withRoleForTenant(user, tenantId, 'admin'),
      accessible_tenant_ids: addTenant(user, tenantId),
    };
    await ctx.userRepo.update(updated);
    if (ctx.auth.getSession()?.user_id === userId) {
      ctx.auth.adoptSessionGrants(sessionGrantsFromUser(updated));
    }
    logger.info({ event: 'cabinet_access_granted_on_activation', tenantId });
    return { ok: true, user: updated };
  }

  /**
   * « Activer et ajouter » depuis le portefeuille : la clé de l'entreprise `addedTenantId` vient d'être activée.
   * L'utilisateur connecté, qui en a prouvé la possession, en reçoit l'accès administrateur — sinon il ne pourrait
   * pas ouvrir le dossier qu'il vient d'ajouter. À appeler depuis son dossier d'origine (sa fiche y est lisible).
   */
  static async grantAfterClientActivation(
    ctx: AppContext,
    sessionBefore: { user_id: string; tenant_id: string } | null | undefined,
    addedTenantId: string | null | undefined,
  ): Promise<CabinetAccessResult> {
    if (!sessionBefore) return { ok: false, error: 'Aucune session ouverte.' };
    if (!addedTenantId) return { ok: false, error: 'Aucune entreprise ajoutée.' };
    return CabinetAccessService.grantAfterKeyActivation(ctx, sessionBefore.user_id, addedTenantId);
  }
}
